import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsApi } from '../services/api';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

// For components that may also render outside the provider (shared pages,
// desktop panels): null instead of throwing.
export const useOptionalTheme = () => useContext(ThemeContext);

const applyDarkClass = (dark) => {
  if (dark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// Public/landing pages are always dark; user theme preference only applies inside the app.
const isAppRoute = (pathname) => pathname === '/app' || pathname.startsWith('/app/');

const effectiveTheme = (pathname, userTheme) => (isAppRoute(pathname) ? userTheme : 'dark');

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem('bb_theme') || 'dark';
    const initialPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    applyDarkClass(effectiveTheme(initialPath, stored) === 'dark');
    return stored;
  });

  useEffect(() => {
    applyDarkClass(effectiveTheme(location.pathname, theme) === 'dark');
  }, [location.pathname, theme]);

  const setTheme = useCallback(async (t) => {
    setThemeState(t);
    localStorage.setItem('bb_theme', t);
    try {
      // Dotted path - sending the whole `ui_preferences` object would $set-overwrite
      // the entire subtree on the server, wiping view catalogs and per-project view prefs.
      await settingsApi.update({ 'ui_preferences.theme': t });
    } catch {  // offline or signed out: the theme is already on and kept on this device
    }
  }, []);

  const syncFromBackend = useCallback((backendTheme) => {
    if (!backendTheme) return;
    setThemeState((current) => {
      if (backendTheme !== current) {
        localStorage.setItem('bb_theme', backendTheme);
        return backendTheme;
      }
      return current;
    });
  }, []);

  // The provider draws again on every navigation (it follows the path): the same value
  // keeps every useTheme() caller from drawing with it.
  const value = useMemo(() => ({ theme, setTheme, syncFromBackend }), [theme, setTheme, syncFromBackend]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
